import { useCallback } from 'react';

import { useGetWhiteboardLibraryQuery, useSaveSchoolLibraryMutation } from 'core/graphql';

export interface IWhiteboardLibraryData {
  itemsJson: string;
  version: number;
}

export interface ISaveLibraryResult {
  success: boolean;
  errorCode: string | null;
  /** New version on success; the server's CURRENT version on VERSION_CONFLICT. */
  version: number | null;
}

/**
 * The school-wide component library (spec 0010 rev.2) — one shared row for
 * everyone, optimistic-locked like the board scene. TEACHER-only on the
 * server; only the editor mounts this hook.
 */
export const useWhiteboardLibrary = () => {
  const { data, loading, refetch } = useGetWhiteboardLibraryQuery({
    fetchPolicy: 'network-only',
  });

  const refetchLibrary = useCallback(async (): Promise<IWhiteboardLibraryData | null> => {
    const { data: fresh } = await refetch();
    return fresh?.whiteboardLibrary ?? null;
  }, [refetch]);

  return {
    library: data?.whiteboardLibrary ?? null,
    libraryLoading: loading,
    refetchLibrary,
  };
};

export const useSaveWhiteboardLibrary = () => {
  const [mutate, { loading }] = useSaveSchoolLibraryMutation();

  const saveLibrary = useCallback(
    async (itemsJson: string, expectedVersion: number): Promise<ISaveLibraryResult> => {
      const { data } = await mutate({ variables: { itemsJson, expectedVersion } });
      const payload = data?.saveWhiteboardLibrary;
      return {
        success: Boolean(payload?.success),
        errorCode: payload?.customErrors?.[0]?.code ?? null,
        version: payload?.version ?? null,
      };
    },
    [mutate],
  );

  return { saveLibrary, saving: loading };
};
